<template>
  <div id="app">
    <van-config-provider>
      <nav-bar />
      <router-view v-slot="{ Component }">
        <section class="app-wrapper">
          <keep-alive :include="keepAliveRouteNames">
            <component :is="Component" />
          </keep-alive>
          <tab-bar />
        </section>
      </router-view>
    </van-config-provider>

    <!-- 维护公告 -->
    <MaintainPopup ref="MaintainPopupRef" />

    <!-- 账户在其他设备登录提示 -->
    <LembretePopup ref="LembretePopupRef" />

    <!-- 提现公告 -->
    <TopUpPopup ref="TopUpPopupRef" />

    <!-- 图片公告 -->
    <ImagesPopup ref="ImagesPopupRef" @close="handleGetSiteMessageList" />

    <!-- 活动公告 -->
    <AtividadePopup ref="AtividadePopupRef" @close="handleAtividadePopupClose">
      <template #background>
        <RedEnvelopeRain />
      </template>
    </AtividadePopup>

    <!-- 咨询公告 -->
    <SiteMessagePopup ref="SiteMessagePopupRef" />

    <!-- 充值爆金币 -->
    <GoldCoinsBurstPopup
      ref="GoldCoinsBurstPopupRef"
      @close="handleGoldCoinsBurstPopupClose"
      @deposito="handleGoldCoinsBurstPopupDeposito"
    />

    <!-- 七天签到 -->
    <SevenDayCheckInPopup ref="SevenDayCheckInPopupRef" @close="handleSevenDayCheckInPopupClose" />

    <!-- 安全认证 -->
    <SecurityCertificationPopup ref="SecurityCertificationPopupRef" @close="handleSecurityCertificationPopupClose" />

    <!-- 下载pwa弹窗 -->
    <DownloadPopup ref="DownloadPopupRef" @download-guide="handleDownloadGuidePopupOpen" />

    <!-- 下载pwa安装引导弹窗 -->
    <DownloadGuidePopup ref="DownloadGuidePopupRef" />

    <!-- 登录注册弹窗 -->
    <LoginRegisterPopup ref="LoginRegisterRef" @close="handlePopupOpen" />

    <!-- 页面切换 loading（TabBar 等配置页，不含首页；） -->
    <van-loading v-if="isPageLoading" vertical class="page-loading-overlay"></van-loading>
  </div>
</template>

<script setup>
import { STORAGE_LANG_KEY, STORAGE_TOKEN_KEY, STORAGE_USER_INFO_KEY } from '@/stores/mutation-type'
import { bindAccountOwnershipApi } from '@/api/profile/userInfo'
import { useRouteCacheStore } from '@/stores'
import { useI18n } from 'vue-i18n'
import { initWebSocket } from './utils/websocketService'
import { useWebSocketStore } from '@/stores/websocketStore'
import { useEventBus } from '@vueuse/core'
import pinia from '@/stores'
import { useRouter } from 'vue-router'
import { closeWebSocket } from '@/utils/websocketService'
import { claimJoinLoginApi } from '@/api/activity/index'
import { useUserStore, useBalanceStore } from '@/stores'
import { userBalanceApi } from '@/api/deposito'
import { RECHARGE_CLAIM_TYPE } from '@/components/HomeAdPopup/SevenDayCheckIn/components/RechargeClaim/js/index'
import { templateTypeWin2FlagWithAppSeries } from '@/utils/templateType'

const isPWA = ref(false)
const router = useRouter()
const route = useRoute()
const { t } = useI18n()
const actiTimer = ref(null) // 活动接口定时器
const AtividadeImagesPopupChecked = ref(window.localStorage.AtividadeImagesPopupChecked || 'null')
const LoginRegisterRef = ref(null) //  登录注册弹窗
const openLoginPopup = useEventBus('open-login-popup')

// 计算属性获取appid
// const appid = computed(() => {
//   console.log('app-vue =computed')
//   return JSON.parse(localStorage.getItem('localConfig')).appid
// })
// const is52000Series = computed(() => appid.value >= 52000 && appid.value < 53000)

const is52000Series = ref(false)

/**
 * 账户在其他设备登录提示相关
 */
const MaintainPopupRef = ref(null)
const handleMaintainPopupOpen = () => {
  nextTick(() => {
    MaintainPopupRef.value.open()
  })
}

/**
 * 账户在其他设备登录提示相关
 */
const LembretePopupRef = ref(null)
const handleLembretePopupOpen = () => {
  nextTick(() => {
    LembretePopupRef.value.open()
  })
}

/**
 * 提现公告弹窗相关
 */
const TopUpPopupRef = ref(null)
const handleTopUpPopupOpen = (msg) => {
  nextTick(() => {
    TopUpPopupRef.value.open(msg)
  })
}

/**
 * 图片公告弹窗相关
 */
const ImagesPopupRef = ref(null)
const handleImagesPopupOpen = () => {
  nextTick(() => {
    ImagesPopupRef.value.open()
  })
}
const handleGetImagesList = () => {
  nextTick(() => {
    ImagesPopupRef.value.getList()
  })
}

/**
 * 活动公告弹窗相关
 */
const AtividadePopupRef = ref(null)
/** 活动公告关闭后队列推进中，防重复 close 并发推进 */
let atividadeCloseHandling = false
const handleAtividadePopupOpen = () => {
  nextTick(() => {
    AtividadePopupRef.value.open()
  })
}
const handleGetAtividadeList = async () => {
  await nextTick()

  let opened = false
  try {
    opened = await AtividadePopupRef.value?.getList()
  } catch (error) {
    console.error('[App] get atividade list error:', error)
  }

  // 活动公告被今日免打扰跳过、无数据或请求失败时，继续登录后队列
  if (!opened && hadToken.value && is52000Series.value) {
    await handleSevenDayCheckInPopupOpen()
  }
  return opened
}
const handleAtividadePopupClose = () => {
  if (atividadeCloseHandling) return
  atividadeCloseHandling = true

  nextTick(async () => {
    try {
      if (hadToken.value) {
        // handleDownloadPopupOpen()
        await handleSevenDayCheckInPopupOpen()
      } else {
        handlePreDownloadPopupOpen()
      }
    } finally {
      atividadeCloseHandling = false
    }
  })
}

/**
 * 咨询公告弹窗相关
 */
const SiteMessagePopupRef = ref(null)
const handleSiteMessagePopupOpen = () => {
  nextTick(() => {
    SiteMessagePopupRef.value.open()
  })
}
const handleGetSiteMessageList = () => {
  nextTick(() => {
    SiteMessagePopupRef.value.getList()
  })
}

/**
 * 充值爆金币弹窗相关
 */
const GoldCoinsBurstPopupRef = ref(null)
const handleGoldCoinsBurstPopupOpen = () => {
  nextTick(() => {
    GoldCoinsBurstPopupRef.value?.open()
  })
}
const handleGoldCoinsBurstPopupClose = () => {
  nextTick(() => {
    if (hadToken.value) {
      handleGetAtividadeList()
    } else {
      handlePreDownloadPopupOpen()
    }
  })
}

const handleGoldCoinsBurstPopupDeposito = () => {
  nextTick(() => {
    router.push({ name: 'Deposito' })
  })
}

/**
 * 七天签到弹窗相关
 * 登录后队列：资讯公告 → 签到闭环 → 下载弹窗
 * 签到闭环未真正结束前不会 emit close，因此不会提前弹下载
 */
const SevenDayCheckInPopupRef = ref(null)
/** 签到打开进行中，防重复推进队列 */
let sevenDayCheckInOpening = false
const handleSevenDayCheckInPopupOpen = async (type = '', messageBody = {}) => {
  if (sevenDayCheckInOpening) return
  sevenDayCheckInOpening = true

  try {
    await nextTick()

    switch (type) {
      case RECHARGE_CLAIM_TYPE.RECHARGE:
        SevenDayCheckInPopupRef.value?.openRechargeClaim()
        return
      case RECHARGE_CLAIM_TYPE.REDEEMED:
        SevenDayCheckInPopupRef.value?.openRedeemedClaim(messageBody)
        return
      default: {
        // 签到弹窗未弹出（今日不再展示 / 非首页 / 活动未开）时，直接继续下载弹窗
        const opened = await SevenDayCheckInPopupRef.value?.open()
        if (!opened) {
          handlePreDownloadPopupOpen()
        }
        return
      }
    }
  } finally {
    sevenDayCheckInOpening = false
  }
}
/** 签到整条闭环结束后再弹下载 */
const handleSevenDayCheckInPopupClose = () => {
  nextTick(() => {
    handlePreDownloadPopupOpen()
  })
}

/**
 * 安全认证弹窗相关
 * - 52000 系列：作为下载弹窗前的固定网关
 * - 其他系列：作为独立弹窗直接展示
 */
const SecurityCertificationPopupRef = ref(null)
let securityPopupNextAction = null
const completeSecurityPopupFlow = () => {
  const nextAction = securityPopupNextAction
  securityPopupNextAction = null
  if (nextAction === 'download' && !isPWA.value) {
    handleDownloadPopupOpen()
  }
}
const handleSecurityCertificationPopupOpen = (nextAction = null) => {
  securityPopupNextAction = nextAction
  nextTick(() => {
    const opened = SecurityCertificationPopupRef.value?.open()
    // 今日已勾选或当前不在首页时，跳过安全认证并继续原队列
    if (!opened) completeSecurityPopupFlow()
  })
}
const handleSecurityCertificationPopupClose = () => {
  completeSecurityPopupFlow()
}
const handlePreDownloadPopupOpen = () => {
  handleSecurityCertificationPopupOpen('download')
}

/**
 * 下载pwa弹窗相关
 */
const DownloadPopupRef = ref(null)
const handleDownloadPopupOpen = () => {
  nextTick(() => {
    DownloadPopupRef.value?.open()
  })
}

/**
 * 下载引导弹窗相关
 */
const DownloadGuidePopupRef = ref(null)
const handleDownloadGuidePopupOpen = () => {
  nextTick(() => {
    DownloadGuidePopupRef.value?.open()
  })
}

const handlePopupOpen = () => {
  if (is52000Series.value) {
    // 登录/注册成功由 hadToken 监听启动登录后队列，避免与 close 事件重复触发
    if (!hadToken.value) handleGoldCoinsBurstPopupOpen()
  } else {
    handleSecurityCertificationPopupOpen()
  }
}

// 添加监听器
const handleOpenLoginPopup = () => {
  nextTick(() => {
    LoginRegisterRef.value.openPopupRegLogin()
  })
}
openLoginPopup.on(handleOpenLoginPopup)
const routeCacheStore = useRouteCacheStore()
const keepAliveRouteNames = computed(() => {
  return routeCacheStore.routeCaches
})

/**
 * 页面切换时需要显示 loading 的 name 列表（不含 Home）
 * - 路由页：beforeEach 自动匹配
 * - 全屏弹窗等非路由页：useEventBus('page-loading').emit(name)
 * 后续只需在此追加 name，50000 / 52000 / 53000 系列均生效
 */
const PAGE_LOADING_ROUTE_NAMES = [
  // Tab 一级（除 Home）
  'Deposito',
  'Promocao',
  'Activity',
  'Profile',

  // 充值 / 提现
  'Saque',
  'SaqueSetting',
  'SaquesTarefas',
  'SaquesDetails',
  'SaquesApostasDetalhes',
  'DepositHistory',
  'DepositoRecord', // 全屏弹窗，非路由，需配合 page-loading 事件
  'WithdrawalHistory',
  'BettingHistory',
  'Registros',
  'ContaDetails',
  'Carteira',

  // 个人中心 / 安全
  'UserInfo',
  'Avatar',
  'nickname',
  'Seguranca',
  'Telefone',
  'EMail',
  'Google',
  'SenhaLogin',
  'SenhaSaque',
  'Pergunta',
  'AccountCenter',
  'ChangePassword',
  'TransactionPassword',
  'Dispositivo',
  'ForgotPassword',
  'Settings',

  // 消息
  'Messages',
  'MessageDetails',
  'Mail',

  // 活动详情
  'acti-info',
  'acti-info-two',
  'acti-info-three',
  'acti-info-four',
  'Bonus-de-novo-jogador',
  'taxt-de-info',
  'meus-sub',
  'Roleta',
  'Lottery-BlindBoxes-iPhone17',
  'CheckIn',

  // 游戏 / 搜索
  'Games',
  // 'vendorearch', // 页面内已有列表 loading，避免首次进入叠两个
]
const isPageLoading = ref(false)
let pageLoadingTimer = null
const PAGE_LOADING_MIN_MS = 300

const hidePageLoading = () => {
  if (pageLoadingTimer) {
    clearTimeout(pageLoadingTimer)
    pageLoadingTimer = null
  }
  isPageLoading.value = false
}

const showPageLoading = () => {
  if (pageLoadingTimer) {
    clearTimeout(pageLoadingTimer)
    pageLoadingTimer = null
  }
  isPageLoading.value = true
  nextTick(() => {
    pageLoadingTimer = setTimeout(() => {
      isPageLoading.value = false
      pageLoadingTimer = null
    }, PAGE_LOADING_MIN_MS)
  })
}

/**
 * 非路由「二级页」（如全屏弹窗）也可唤起 loading：
 * useEventBus('page-loading').emit('DepositoRecord')
 * 仅当 name 在 PAGE_LOADING_ROUTE_NAMES 中时生效
 */
const pageLoadingBus = useEventBus('page-loading')
const handlePageLoadingEmit = (name) => {
  if (name && PAGE_LOADING_ROUTE_NAMES.includes(String(name))) {
    showPageLoading()
  }
}
pageLoadingBus.on(handlePageLoadingEmit)

router.beforeEach((to, from) => {
  // 仅路由 name 变化且目标在配置列表时唤起；首次进入 / 同页不显示
  if (from.name && to.name && to.name !== from.name && PAGE_LOADING_ROUTE_NAMES.includes(String(to.name))) {
    showPageLoading()
  } else if (!PAGE_LOADING_ROUTE_NAMES.includes(String(to.name || ''))) {
    hidePageLoading()
  }
})

// 初始化 WebSocket
const websocketUrl = ref(null) //
const userStore = useUserStore()
const balanceStore = useBalanceStore()
const hadToken = computed(() => {
  return !!userStore.userInfo.token
})

// 动态加载主题函数
function loadTheme() {}

const handleGetUserBalance = async () => {
  await userBalanceApi().then((res) => {
    balanceStore.setBalance(res.data.balance)
  })
}

function initWebSocketFun() {
  // 只有登录了才初始化 WebSocket
  if (hadToken.value && JSON.parse(localStorage.getItem('initConfig'))?.ws_url) {
    websocketUrl.value = JSON.parse(localStorage.getItem('initConfig')).ws_url
    initWebSocket('wss://' + websocketUrl.value, async (message) => {
      // console.log('Received WebSocket message:', message)
      // 可以在这里触发全局事件或更新状态
      if (message.type == 'connected' && message?.client_id) {
        // clientId 绑定用户ID
        const clientId = message.client_id
        localStorage.setItem('clientId', clientId)
        // 直接调用useWebSocketStore的bindUser方法
        const websocketStore = useWebSocketStore(pinia)
        try {
          console.log('initWebSocketFun...success...')

          await websocketStore.bindUser(clientId)
          // console.log('main ts clientId bound to WebSocket connection:', clientId)
        } catch (error) {
          console.error('main ts  Failed to bind clientId to WebSocket:', error)
        }
      }

      if (message.type == 'mailReceive' && message?.body) {
        // 如果用户在邮件页面，则广播通知邮件页面插入数据，如果不在邮件页面需要全局弹窗提示吗
        const currentRoute = router.currentRoute.value.name
        if (currentRoute === 'Mail') {
          // 发送事件通知邮件页面
          const mailEventBus = useEventBus('mail-received')
          mailEventBus.emit(message.body)
        } else {
          // 显示全局通知
          const notificationEventBus = useEventBus('global-notification')
          notificationEventBus.emit({
            type: 'mail',
            message: message.body.title || t('home-ad-popup-index.app.newEmail'),
          })
        }
      }

      if (message.type == 'home_top' && message?.body) {
        showNotify({
          type: 'primary',
          message: message.body.title + ' : ' + message.body.content || '-----',
        })
      }

      // 系统维护时
      if (message.type == 'maintain' && message?.body) {
        handleMaintainPopupOpen()
        return false
      }

      // 账号在其他设备登录时
      if (message.type == 'unbind' && message?.body) {
        handleLembretePopupOpen()
        return false
      }

      // 提现成功时
      if (message.type == 'withdraw' && message?.msg) {
        handleTopUpPopupOpen(message.msg)
        return false
      }

      // 当接受到站内消息时
      if (message.type == 'site_message' && message?.body) {
        nextTick(() => {
          // console.log('site_message...')
          handleGetSiteMessageList()
          // handleSevenDayCheckInPopupOpen('redeemed', message?.body)
        })
      }

      // 当接受到七天签到消息时  (  biz_type = 1 表示签到充值)
      if (
        message.type == 'recharge_success' &&
        message?.body &&
        message?.body?.biz_type == 1 &&
        message?.body &&
        (message?.body?.status == 3 || message?.body?.status == 1)
      ) {
        nextTick(() => {
          // 判断下当前页面位置，如果是首页或者我的页面，则弹窗
          console.log('recharge_success...签到')
          // 触发获取余额接口，更新余额
          handleGetUserBalance()
          handleSevenDayCheckInPopupOpen('redeemed', message?.body)
        })
      }

      // 常规充值成功时弹窗提示  (biz_type = 0 表示常规充值 还有其他字段需要补充 )
      // if (message.type == 'recharge_success' && message?.body && message?.body?.biz_type == 0 && xxxxx ) {
      //   nextTick(() => {
      //     // console.log('recharge_success...常规充值')
      //     // 后续补充
      //   })
      // }
    })
  }
}

// 国际化
import { locale, langEnums } from '@/utils/i18n'
const getLocale = () => {
  locale.value = localStorage.getItem(STORAGE_LANG_KEY) || langEnums.ES_MX
}
// 初始化
function initAppVue() {
  document.querySelector('.loadingbg').style.display = 'none'
  // 获取并存储邀请码
  const urlParams = new URLSearchParams(window.location.search)
  const inviteCode = urlParams.get('id') || ''
  const channelId = urlParams.get('channel_id') || ''
  const businessId = urlParams.get('business_id') || ''
  if (inviteCode || channelId || businessId) {
    localStorage.setItem('id', inviteCode || '')
    localStorage.setItem('channel_id', channelId || '')
    localStorage.setItem('business_id', businessId || '')
  } else {
    // 如果不存在分享链接  不移除  继续执上一个分享链接的参数
    // localStorage.removeItem('id')
    // localStorage.removeItem('channel_id')
    // localStorage.removeItem('business_id')
  }

  getLocale()

  // 未登录主动弹出注册
  // if (!hadToken.value) {
  //   nextTick(() => {
  //     LoginRegisterRef.value?.openPopupRegLogin('register')
  //   })
  // } else {
  //   if (appid.value >= 52000) {
  //     const GoldCoinsBurstPopupChecked = window.localStorage.GoldCoinsBurstPopupChecked || 'null'
  //     const AtividadePopupChecked = window.localStorage.AtividadePopupChecked || 'null'
  //     if (GoldCoinsBurstPopupChecked === 'null' || GoldCoinsBurstPopupChecked !== 'checked') {
  //       handleGoldCoinsBurstPopupOpen()
  //     } else if (AtividadePopupChecked === 'null' || AtividadePopupChecked !== 'checked') {
  //       handleGetAtividadeList()
  //     }
  //   }
  // }
}

const senBindAccountOwnershipApi = async () => {
  const url = new URL(window.location.href)
  const urlParams = url.searchParams
  let inviteCode = urlParams.get('id') || ''
  let channelId = urlParams.get('channel_id') || ''
  let businessId = urlParams.get('business_id') || ''
  if (inviteCode || channelId || businessId) {
    localStorage.setItem('id', inviteCode || '')
    localStorage.setItem('channel_id', channelId || '')
    localStorage.setItem('business_id', businessId || '')
  } else {
    inviteCode = localStorage.getItem('id') || ''
    channelId = localStorage.getItem('channel_id') || ''
    businessId = localStorage.getItem('business_id') || ''
  }
  const sendShareIds = {
    id: inviteCode,
    channel_id: channelId,
    business_id: businessId,
  }
  const ttclid = urlParams.get('ttclid') || ''
  const fbclid = urlParams.get('fbclid') || ''
  sendShareIds.ttclid = ttclid
  sendShareIds.fbclid = fbclid
  urlParams.delete('ttclid')
  urlParams.delete('fbclid')
  window.history.replaceState(null, null, url.toString())
  // console.log('sendShareIds:appvue', sendShareIds)
  // console.log('sendShareIds:appvue', urlParams)
  // 上报分享信息
  await bindAccountOwnershipApi(sendShareIds)
}

// 监听登录状态变化，当登录时初始化WebSocket
watch(
  hadToken,
  (newToken) => {
    if (newToken) {
      console.log('app-vue =watch')
      initWebSocketFun()
      senBindAccountOwnershipApi()
      if (is52000Series.value) {
        const GoldCoinsBurstPopupChecked = window.localStorage.GoldCoinsBurstPopupChecked || 'null'
        const AtividadePopupChecked = window.localStorage.AtividadePopupChecked || 'null'
        if (GoldCoinsBurstPopupChecked === 'null' || GoldCoinsBurstPopupChecked !== 'checked') {
          handleGoldCoinsBurstPopupOpen()
        } else if (AtividadePopupChecked === 'null' || AtividadePopupChecked !== 'checked') {
          handleGetAtividadeList()
        } else {
          handleSevenDayCheckInPopupOpen()
        }
      } else {
        handleSecurityCertificationPopupOpen()
      }
    } else {
      nextTick(() => {
        // LoginRegisterRef.value?.openPopupRegLogin('register')
        if (is52000Series.value) {
          const GoldCoinsBurstPopupChecked = window.localStorage.GoldCoinsBurstPopupChecked || 'null'
          if (GoldCoinsBurstPopupChecked === 'null' || GoldCoinsBurstPopupChecked !== 'checked') {
            handleGoldCoinsBurstPopupOpen()
          } else {
            handlePreDownloadPopupOpen()
          }
        } else {
          handleSecurityCertificationPopupOpen()
        }
      })
    }
  },
  { immediate: true }
)

async function actiInitData() {
  // 是否有token
  if (!localStorage.getItem(STORAGE_TOKEN_KEY)) {
    return
  }
  const { data, code, msg } = await claimJoinLoginApi()
  if (code == 1 && data.is_popup == 1) {
    successToast(msg || t('home-ad-popup-index.app.requestSuccess'))
  }
}
onMounted(async () => {
  console.log('app-vue =onMounted')
  is52000Series.value = await templateTypeWin2FlagWithAppSeries()
  isPWA.value = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true
  // console.log('onMounted-isPWA:', isPWA.value)

  initAppVue()
  // const localConfig = JSON.parse(localStorage.getItem('localConfig'))
  // const iconUrl = '/theme/logo/favicon.ico'
  // const iconUrlApple = '/theme/logo/pwa-192x192.png'
  // useHead({
  //   title: () => localConfig.appName, // localConfig.appName t('app.name')
  //   meta: [
  //     {
  //       name: 'description',
  //       content: () => localConfig.appName, // localConfig.appDescription
  //     },
  //   ],
  //   link: [
  //     {
  //       rel: 'icon',
  //       type: 'image/x-icon',
  //       href: iconUrl, // 动态设置
  //     },
  //     {
  //       rel: 'apple-touch-icon',
  //       href: iconUrlApple, // 动态设置 Apple Touch Icon
  //     },
  //   ],
  // })

  // if (hadToken.value) {
  //   handleGetImagesList()
  // }

  //每十分钟调用一次
  actiTimer.value = setInterval(() => {
    actiInitData()
  }, 600000) // 10分
})

onUnmounted(() => {
  // 清理活动接口定时器
  if (actiTimer.value) {
    clearInterval(actiTimer.value)
  }
  openLoginPopup.off(handleOpenLoginPopup)
  pageLoadingBus.off(handlePageLoadingEmit)
  hidePageLoading()
})
</script>

<style scoped lang="less">
.logo1-img {
  width: 234px;
  height: 70px;
}

.app-wrapper {
  width: 100%;
  position: relative;
  overflow: hidden;
}

/* 与首页 van-loading 蒙层一致；高于全屏弹窗（van-overlay ~2000） */
.page-loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: var(--home-loading-overlay);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: 3000;
  overflow: hidden;
}

/* 初始加载页面样式 */
.initial-loading {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: #ffffff;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}
</style>
